import React, { JSXElementConstructor, ReactElement } from 'react';
import { setVisited as setVisitedAction } from 'actions/events';
import {
  IEvents,
  MapType,
  MapLessonItemProps,
  IMapLessonItemStates,
} from 'types';
import { formatTime, getFinishTime } from 'utils/helpers/momentTime';
import { connect } from 'react-redux';
import Logger from '../helpers/devConsoleLog';
import MapLessonItemBase from './mapLessonItemBase';

type ItemProps = {
  MapItem: React.FC<IMapLessonItemStates>;
  lessons: IEvents[];
  mapType: MapType;
  setVisited: (unitId: number, eventId: number) => void;
  unitId: number | undefined;
};

class MapLessonItem extends React.Component<ItemProps, MapLessonItemProps> {
  private interval!: NodeJS.Timeout;

  private intervalStepMS = 1 * 1000;

  private activeEventId: number | undefined;

  constructor(props: ItemProps) {
    super(props);

    this.state = {
      url: '',
      active: false,
      visited: false,
      finishTime: 0,
      process: false,
    };
  }

  componentDidMount() {
    this.interval = setInterval(() => this.onTick(), this.intervalStepMS);
    this.onTick();
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  onTick() {
    let active = false;
    let process = false;
    let visited = false;
    let completed = true;
    let url = '';

    const { lessons, mapType } = this.props;
    const mapIsStatic = mapType === MapType.Static;

    if (lessons) {
      const nowMS = new Date().getTime();

      lessons.forEach((l) => {
        url = l.link || '';
        const startTime: number = formatTime(l.startTimeStr);
        const finishTime = getFinishTime(l.startTimeStr, l.endTimeStr);
        this.setState({ finishTime, url: l.link || '' });

        const lessonIsActive = nowMS > startTime && nowMS <= finishTime;
        // at least one lesson is active
        if (lessonIsActive) {
          active = true;
          this.activeEventId = l.id;
        }

        if (!mapIsStatic) {
          visited = l.isStarted || false;
        } else {
          // at least one lesson isn't finished
          // TODO: remove it according to new requirements ?
          if (nowMS < finishTime) {
            completed = false;
          }

          // lessons finished and not visited
          if (nowMS > finishTime && !l.isStarted) {
            completed = false;
          }
        }
      });

      if (mapIsStatic) {
        const isOneFinished = lessons.some((l) => {
          const finishTime = getFinishTime(l.startTimeStr, l.endTimeStr);
          return nowMS > finishTime;
        });
        const isOneNotStarted = lessons.some((l) => {
          const startTime = formatTime(l.startTimeStr);
          return nowMS < startTime;
        });

        // at least one finished and at least one not started yet
        if (isOneFinished && isOneNotStarted) {
          process = true;
        }

        const isOneVisited = lessons.some((l) => l.isStarted);
        const isAllFinished = lessons.every((l) => {
          const finishTime = getFinishTime(l.startTimeStr, l.endTimeStr);
          return nowMS > finishTime;
        });

        // at least one lesson visited and all lessons finished
        if (isOneVisited && isAllFinished) {
          completed = true;
        }
      }

      const isSomethingChanged = (() => {
        const { state } = this;

        return (
          active !== state.active ||
          process !== state.process ||
          (mapIsStatic ? completed : visited) !== state.visited ||
          url !== state.url
        );
      })();

      if (isSomethingChanged) {
        this.setState({
          active,
          process,
          // map with road green checkmarks means visited
          // map without road green checkmarks means completed
          visited: mapIsStatic ? completed : visited,
          url,
        });
      }

      // Logger('lessons:', lessons);
    }
  }

  onClick = () => {
    const { lessons, setVisited, unitId } = this.props;
    Logger('lessons:', lessons, '\nactiveEventId:', this.activeEventId);
    if (unitId && this.activeEventId) {
      setVisited(unitId, this.activeEventId);
    }
  };

  render() {
    const { url, active, visited, process, finishTime } = this.state;
    const { MapItem } = this.props;

    return (
      <MapLessonItemBase
        url={url}
        active={active}
        finishTime={finishTime}
        svgElement={
          <MapItem active={!!active} visited={!!visited} process={!!process} />
        }
        onClickCb={this.onClick}
      />
    );
  }
}

const WrappedMapLessonItemHOC = connect(null, {
  setVisited: setVisitedAction,
})(MapLessonItem);

export default function mapLessonItemHOC(
  MapItem: React.FC<IMapLessonItemStates>,
  lessons: IEvents[],
  mapType: MapType,
  unitId: number | undefined
): ReactElement<typeof MapLessonItem, string | JSXElementConstructor<unknown>> {
  return (
    <WrappedMapLessonItemHOC
      MapItem={MapItem}
      lessons={lessons}
      mapType={mapType}
      unitId={unitId}
    />
  );
}
